import React, { useEffect, useState } from 'react';
import { useFolderNavigation } from '../contexts/FolderNavigationContext';
import { Folder } from '../types';
import { folderService } from '../services/api';
import icons from './Icons';

interface FolderNode extends Folder {
  children: FolderNode[];
}

const FolderTreeNavigation: React.FC = () => {
  const [folderTree, setFolderTree] = useState<FolderNode[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [foldersLoading, setFoldersLoading] = useState(true);

  const { selectedFolder, handleFolderClick, handleBackToFolders } = useFolderNavigation();

  useEffect(() => {
    loadAllFolders();
  }, []);

  // Efecto para expandir automáticamente todas las carpetas padre cuando se carga el árbol
  useEffect(() => {
    if (folderTree.length > 0) {
      const allFolders = flattenFolderTree(folderTree);
      const foldersWithChildren = new Set<number>();
      
      // Identificar todas las carpetas que tienen hijos
      allFolders.forEach(folder => {
        const hasChildren = allFolders.some(f => f.parent_folder === folder.pk_folder);
        if (hasChildren) {
          foldersWithChildren.add(folder.pk_folder);
        }
      });
      
      // Expandir todas las carpetas que tienen hijos
      setExpandedFolders(foldersWithChildren);
    }
  }, [folderTree]);

  const loadAllFolders = async () => {
    try {
      setFoldersLoading(true);
      const allFolders = await folderService.getAllFolders();
      console.log('Todas las carpetas cargadas:', allFolders);
      const tree = buildFolderTree(allFolders);
      console.log('Arbol de carpetas construido:', tree);
      setFolderTree(tree);
    } catch (error) {
      console.error('Error loading folders for sidebar:', error);
    } finally {
      setFoldersLoading(false);
    }
  };

  const buildFolderTree = (folders: Folder[]): FolderNode[] => {
    const folderMap = new Map<number, FolderNode>();
    const rootFolders: FolderNode[] = [];

    // Crear nodos para todas las carpetas
    folders.forEach(folder => {
      folderMap.set(folder.pk_folder, { ...folder, children: [] });
    });

    // Construir la jerarquía
    folders.forEach(folder => {
      const node = folderMap.get(folder.pk_folder)!;
      
      if (folder.parent_folder) {
        const parent = folderMap.get(folder.parent_folder);
        if (parent) {
          parent.children.push(node);
          console.log(`Agregando '${folder.name}' como hijo de '${parent.name}'`);
        } else {
          console.warn(`Carpeta padre con ID ${folder.parent_folder} no encontrada para '${folder.name}'`);
        }
      } else {
        rootFolders.push(node);
        console.log(`Agregando '${folder.name}' como carpeta raíz`);
      }
    });

    console.log('Carpetas raíz finales:', rootFolders.map(f => f.name));
    return rootFolders;
  };

  const findPathToFolder = (folders: Folder[], targetId: number): number[] => {
    const path: number[] = [];
    let currentFolder = folders.find(f => f.pk_folder === targetId);
    
    while (currentFolder) {
      if (currentFolder.parent_folder) {
        path.unshift(currentFolder.parent_folder);
        currentFolder = folders.find(f => f.pk_folder === currentFolder!.parent_folder);
      } else {
        break;
      }
    }
    
    return path;
  };

  const flattenFolderTree = (tree: FolderNode[]): Folder[] => {
    const result: Folder[] = [];
    const traverse = (nodes: FolderNode[]) => {
      nodes.forEach(node => {
        result.push(node);
        if (node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(tree);
    return result;
  };

  const toggleFolder = (folderId: number) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const renderFolderNode = (node: FolderNode, level: number = 0) => {
    const isExpanded = expandedFolders.has(node.pk_folder);
    const isSelected = selectedFolder?.pk_folder === node.pk_folder;
    const hasChildren = node.children.length > 0;

    return (
      <div key={node.pk_folder}>
        <div
          className={`sidebar-nav-item ${isSelected ? 'active' : ''} ${level > 0 ? 'sidebar-nav-item-child' : ''} relative`}
          style={{ 
            paddingLeft: `${hasChildren ? 32 + level * 16 : 12 + level * 16}px`,
            marginLeft: level > 0 ? '4px' : '0px'
          }}
          onClick={() => {
            handleFolderClick(node);
            // Si la carpeta tiene hijos, expandirla automáticamente
            if (hasChildren && !isExpanded) {
              toggleFolder(node.pk_folder);
            }
          }}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(node.pk_folder);
              }}
              className="folder-toggle-btn"
              style={{ left: `${8 + level * 16}px` }}
              title={isExpanded ? 'Colapsar carpeta' : 'Expandir carpeta'}
            >
              {isExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  fill="currentColor"
                  className="bi bi-chevron-down"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  fill="currentColor"
                  className="bi bi-chevron-right"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              )}
            </button>
          )}
          
          <span className="sidebar-nav-icon">
            {icons.Folder}
          </span>
          
          <span className="sidebar-nav-text">
            {node.name}
            {hasChildren && (
              <span 
                className="ml-2 text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded-full font-medium"
                title={`${node.children.length} subcarpeta${node.children.length !== 1 ? 's' : ''}`}
              >
                {node.children.length}
              </span>
            )}
          </span>
        </div>

        {isExpanded && hasChildren && (
          <div className="child-folder">
            {node.children.map(child => (
              <div key={child.pk_folder}>
                {renderFolderNode(child, level + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border-t pt-4">
      <div className="folder-tree-container">
        <ul className="sidebar-nav-list">
          {/* Home/Root Navigation */}
          <li>
            <button
              onClick={handleBackToFolders}
              className={`sidebar-nav-item ${!selectedFolder ? 'active' : ''}`}
            >
              <span className="sidebar-nav-icon">
                {icons.Home}
              </span>
              <span className="sidebar-nav-text">BBX-Documentation</span>
            </button>
          </li>

          {foldersLoading ? (
            <li>
              <div className="p-4">
                <div className="animate-pulse space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-8 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="text-xs text-gray-400 mt-2">Cargando carpetas...</div>
              </div>
            </li>
          ) : folderTree.length > 0 ? (
            <>
              {folderTree.map(node => (
                <li key={node.pk_folder}>
                  {renderFolderNode(node)}
                </li>
              ))}
            </>
          ) : (
            <li>
              <div className="p-4 text-center text-gray-500 text-sm">
                <div>No hay carpetas creadas</div>
                <div className="text-xs text-gray-400 mt-1">
                  Carpetas expandidas: {expandedFolders.size}
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default FolderTreeNavigation;